/* ============================================================
   Voonka Coffee Premium PDP — JavaScript
   Accordion, tabs, gallery, scroll animations, radio cards
   ============================================================ */

(function() {
  'use strict';

  // ============================================================
  // Intersection Observer — Fade-in animations
  // ============================================================
  function initScrollAnimations() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('pdp-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.pdp-fade-in').forEach(el => {
      observer.observe(el);
    });
  }

  // ============================================================
  // Accordion
  // Toggle open/close on every click (including X close icon).
  // For non-independent groups (e.g. FAQ), close siblings first.
  // For independent groups (data-independent="true", e.g. collapsibles row),
  // each card toggles individually — X click closes that card.
  // ============================================================
  function initAccordions() {
    document.querySelectorAll('.pdp-accordion-trigger').forEach(trigger => {
      trigger.addEventListener('click', function() {
        const item = this.closest('.pdp-accordion-item');
        const content = item.querySelector('.pdp-accordion-content');
        const isActive = item.classList.contains('active');
        const group = item.closest('.pdp-accordion-group');
        const isIndependent = group && group.dataset.independent === 'true';

        if (isIndependent) {
          // Independent group: simply toggle this card
          if (isActive) {
            item.classList.remove('active');
            if (content) content.classList.remove('active');
            this.setAttribute('aria-expanded', 'false');
          } else {
            item.classList.add('active');
            if (content) content.classList.add('active');
            this.setAttribute('aria-expanded', 'true');
          }
        } else {
          // Non-independent group: close all siblings first
          if (group) {
            group.querySelectorAll('.pdp-accordion-item.active').forEach(openItem => {
              openItem.classList.remove('active');
              const openContent = openItem.querySelector('.pdp-accordion-content');
              if (openContent) openContent.classList.remove('active');
              const openTrigger = openItem.querySelector('.pdp-accordion-trigger');
              if (openTrigger) openTrigger.setAttribute('aria-expanded', 'false');
            });
          }
          // Open this item only if it was not already active
          if (!isActive) {
            item.classList.add('active');
            if (content) content.classList.add('active');
            this.setAttribute('aria-expanded', 'true');
          }
        }
      });
    });
  }

  // ============================================================
  // Tab Switcher
  // ============================================================
  function initTabs() {
    document.querySelectorAll('.pdp-tab-nav').forEach(nav => {
      const tabs = nav.querySelectorAll('.pdp-tab-btn');
      const panels = nav.closest('.pdp-tabs-wrapper').querySelectorAll('.pdp-tab-panel');

      tabs.forEach(tab => {
        tab.addEventListener('click', function() {
          const target = this.dataset.tab;

          tabs.forEach(t => t.classList.remove('active'));
          panels.forEach(p => p.classList.remove('active'));

          this.classList.add('active');
          const panel = nav.closest('.pdp-tabs-wrapper').querySelector(`[data-panel="${target}"]`);
          if (panel) panel.classList.add('active');
        });
      });
    });
  }

  // ============================================================
  // Product Gallery — Main image + thumbnails
  // ============================================================
  function initGallery() {
    document.querySelectorAll('.pdp-gallery').forEach(gallery => {
      const mainImg = gallery.querySelector('.pdp-gallery-main img');
      const thumbs = gallery.querySelectorAll('.pdp-gallery-thumb');

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', function() {
          const src = this.dataset.src;
          const alt = this.dataset.alt || '';
          if (mainImg && src) {
            mainImg.src = src;
            mainImg.alt = alt;
          }
          thumbs.forEach(t => t.classList.remove('active'));
          this.classList.add('active');
        });
      });

      // Activate first thumb by default
      if (thumbs[0]) thumbs[0].classList.add('active');
    });
  }

  // ============================================================
  // Radio Cards — Pricing tier selection
  // Listen on 'change' event of the radio input, not 'click' on label.
  // This avoids double-firing when the browser natively toggles the input
  // via the <label> click — using 'change' fires exactly once per selection.
  // ============================================================
  function initRadioCards() {
    document.querySelectorAll('.pdp-radio-card input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', function() {
        if (!this.checked) return;
        const name = this.name;
        // Remove selected from all cards in this group
        document.querySelectorAll(`.pdp-radio-card input[name="${name}"]`).forEach(input => {
          input.closest('.pdp-radio-card').classList.remove('selected');
        });
        // Add selected to the card containing this radio
        const card = this.closest('.pdp-radio-card');
        if (card) {
          card.classList.add('selected');
          // Update CTA button price if data attribute present
          const price = card.dataset.price;
          const ctaBtn = document.querySelector('.pdp-hero-cta-price');
          if (ctaBtn && price) ctaBtn.textContent = price;
        }
      });
    });

    // Ensure default selected card has radio.checked = true on init
    document.querySelectorAll('.pdp-radio-card.selected').forEach(card => {
      const radio = card.querySelector('input[type="radio"]');
      if (radio) radio.checked = true;
    });
  }

  // ============================================================
  // Organ Systems Interactive — Body diagram + accordion sync
  // ============================================================
  function initOrganSystems() {
    const items = document.querySelectorAll('.pdp-organ-item');
    const hotspots = document.querySelectorAll('.pdp-body-hotspot');

    items.forEach(item => {
      item.addEventListener('click', function() {
        const system = this.dataset.system;

        // Update accordion active state
        items.forEach(i => i.classList.remove('active'));
        this.classList.add('active');

        // Sync body diagram hotspot
        hotspots.forEach(h => h.classList.remove('active'));
        const hotspot = document.querySelector(`.pdp-body-hotspot[data-system="${system}"]`);
        if (hotspot) hotspot.classList.add('active');
      });
    });

    hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', function() {
        const system = this.dataset.system;
        hotspots.forEach(h => h.classList.remove('active'));
        this.classList.add('active');

        items.forEach(i => i.classList.remove('active'));
        const item = document.querySelector(`.pdp-organ-item[data-system="${system}"]`);
        if (item) {
          item.classList.add('active');
          item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    });

    // Activate first item by default
    if (items[0]) items[0].click();
  }

  // ============================================================
  // Mobile Sticky CTA — Show after hero scrolls out
  // ============================================================
  function initStickyCTA() {
    const stickyCTA = document.querySelector('.pdp-sticky-cta');
    const hero = document.querySelector('.pdp-hero-section');
    if (!stickyCTA || !hero) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          stickyCTA.classList.add('visible');
        } else {
          stickyCTA.classList.remove('visible');
        }
      });
    }, { threshold: 0 });

    observer.observe(hero);
  }

  // ============================================================
  // FAQ Show More
  // ============================================================
  function initFaqShowMore() {
    const showMoreBtns = document.querySelectorAll('.pdp-faq-show-more');
    showMoreBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const container = this.closest('.pdp-faq-section');
        if (!container) return;
        const hiddenItems = container.querySelectorAll('.pdp-accordion-item.pdp-faq-hidden');
        hiddenItems.forEach(item => item.classList.remove('pdp-faq-hidden'));
        this.style.display = 'none';
      });
    });
  }

  // ============================================================
  // Reviews Show More
  // ============================================================
  function initReviewsShowMore() {
    const showMoreBtns = document.querySelectorAll('.pdp-reviews-show-more');
    showMoreBtns.forEach(btn => {
      btn.addEventListener('click', function() {
        const grid = document.querySelector('.pdp-reviews-grid');
        if (!grid) return;
        grid.querySelectorAll('.pdp-review-card.pdp-review-hidden').forEach(card => {
          card.classList.remove('pdp-review-hidden');
        });
        this.style.display = 'none';
      });
    });
  }

  // ============================================================
  // Collapsibles Row — Open all cards on desktop by default
  // Only applies to .pdp-collapsibles-section (the 3-card row).
  // FAQ accordions and other accordion groups are NOT affected.
  // On mobile (<768px) cards stay collapsed to save vertical space.
  // ============================================================
  function initCollapsiblesDesktopOpen() {
    if (window.innerWidth < 768) return;

    const section = document.querySelector('.pdp-collapsibles-section');
    if (!section) return;

    section.querySelectorAll('.pdp-accordion-item').forEach(function(item) {
      item.classList.add('active');
      const content = item.querySelector('.pdp-accordion-content');
      if (content) content.classList.add('active');
      const trigger = item.querySelector('.pdp-accordion-trigger');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    });
  }

  // ============================================================
  // Init all on DOM ready
  // ============================================================
  function init() {
    initScrollAnimations();
    initAccordions();
    initCollapsiblesDesktopOpen();
    initTabs();
    initGallery();
    initRadioCards();
    initOrganSystems();
    initStickyCTA();
    initFaqShowMore();
    initReviewsShowMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
